/**
 * ROLE-LENS-DESIGN-SYSTEM-V1 -- NextActionBar: the five-second-test carrier.
 * ONE primary next action plus a BOUNDED set of secondary actions, filtered
 * per role audience -- so every role workspace can answer "what is the next
 * action" in five seconds (#313 SHARED CONTRACT).
 *
 * SOURCE SCREENS (extraction register):
 * - PRIMARY: the Patient 360 five-second decision set + next-action band
 *   (frontend/src/dashboard/patients/patient-360-view.ts primaryActionView /
 *   buildPatientNextAction / NO_ACTION_REQUIRED_LABEL, rendered in
 *   patients/PatientRecordPage.tsx "SECTION 2: HIGHLIGHTS -- the five-second
 *   decision set"). Proof status: WALK 21 PASSED 2026-07-15 (golden
 *   source); WALK 18 steps LIVE-RUN PENDING.
 * - SECOND: the Lead 360 next-lawful-action line (record/
 *   RecordHeaderBand.tsx CurrentStateBand "Next action" +
 *   truth.nextLawfulAction.source data attribute). Record band PROVEN live
 *   2026-07-11 (WALK 5 + WALK 6, #252 -- golden source).
 * - THIRD: the doctor lens ContinueCareBand primary clinical action
 *   (doctor/DoctorPatientLensPage.tsx; DOCTOR-WORKSPACE-COMPLETION-V1, no
 *   walkbook WALK yet -- IMPLEMENTATION REFERENCE, UNVALIDATED).
 *
 * DESIGN LAW: exactly ONE primary action (design-language 03 P7 item 5 +
 * P12 -- one primary per section; secondaries are the ghost tier). The
 * secondary set is CAPPED (NEXT_ACTION_BAR_SECONDARY_CAP); overflow is
 * dropped deterministically from the end and the bar stamps
 * data-secondary-dropped so a caller test can see the cut. The bar renders
 * LABELS + caller-passed controls; it derives no business state (the next
 * action is engine-sourced, never re-typed -- 03 P7 item 4).
 *
 * ROLE SHAPING (LES-016: links are role-shaped or absent): every action
 * carries the audiences that lawfully hold its door (default: staff);
 * actions outside the viewer's audience are ABSENT, never disabled ghosts.
 * Unknown or absent audience -> NO actions at all (fail closed, per
 * ui/role-lens.ts) and the honest no-action line renders instead.
 *
 * CLINICAL WIRE LAW (CLAUDE.md "Clinical visibility"): presentation, never
 * the security boundary -- an action's real authority lives in backend
 * route auth (doctrine rule 2 SEE-NOT-DO); this bar only refuses to render
 * doors the caller did not declare lawful for the audience.
 */

import type { ReactNode } from 'react';
import { audienceAllows, STAFF_AUDIENCES, type RoleLensAudience } from './role-lens';

/** The desk-scoped no-action copy (patient-360-view.ts, verbatim;
 *  lockstep-pinned in tests). */
export const NEXT_ACTION_NONE_LABEL = 'No action required';

/** ONE primary + at most three secondaries (03 P12 hierarchy). */
export const NEXT_ACTION_BAR_SECONDARY_CAP = 3;

export interface NextActionDef {
  /** Engine-sourced action label (never re-typed on the screen). */
  readonly label: string;
  /** The rendered control (a role-held door: button/link). Optional --
   *  a label-only next action renders as text (the band idiom). */
  readonly control?: ReactNode;
  /** Audiences holding this door (default: staff). Absent for others. */
  readonly audiences?: readonly RoleLensAudience[];
  /** Provenance of the derivation (nextLawfulAction.source idiom). */
  readonly source?: string;
}

export interface VisibleNextActions {
  readonly primary: NextActionDef | null;
  readonly secondary: readonly NextActionDef[];
  /** How many lawful secondaries were dropped by the cap. */
  readonly droppedSecondaryCount: number;
}

/** THE pure visibility decision (pinned in tests): audience-filtered,
 *  fail-closed on unknown audience, secondaries capped. */
export function visibleNextActions(options: {
  readonly audience: unknown;
  readonly primary?: NextActionDef | null;
  readonly secondary?: readonly NextActionDef[];
}): VisibleNextActions {
  const allowsAction = (action: NextActionDef): boolean =>
    audienceAllows(options.audience, action.audiences ?? STAFF_AUDIENCES);
  const primary =
    options.primary !== undefined &&
    options.primary !== null &&
    allowsAction(options.primary)
      ? options.primary
      : null;
  const lawfulSecondary = (options.secondary ?? []).filter(allowsAction);
  return {
    primary,
    secondary: lawfulSecondary.slice(0, NEXT_ACTION_BAR_SECONDARY_CAP),
    droppedSecondaryCount: Math.max(
      0,
      lawfulSecondary.length - NEXT_ACTION_BAR_SECONDARY_CAP,
    ),
  };
}

export interface NextActionBarProps {
  /** The viewer's audience (unknown/absent -> no actions, fail closed). */
  readonly audience: RoleLensAudience | string | null | undefined;
  /** THE one next action (engine-sourced). Null = honest no-action state. */
  readonly primary?: NextActionDef | null;
  readonly secondary?: readonly NextActionDef[];
  /** Honest copy when no lawful action exists for this audience. */
  readonly noActionLabel?: string;
  /** The bar's micro-label. Defaults to the registered "Next action";
   *  DOCTOR-COCKPIT-VISUAL-CONFORM-V1 round 3 lets the doctor dock wear
   *  the reference's terse "Next" (an ADDITIVE presentation knob on THIS
   *  one control -- every other adopter renders byte-identical). */
  readonly label?: string;
  readonly testId?: string;
}

export default function NextActionBar({
  audience,
  primary,
  secondary,
  noActionLabel,
  label = 'Next action',
  testId,
}: NextActionBarProps): JSX.Element {
  const visible = visibleNextActions({ audience, primary, secondary });
  return (
    <div
      className="ac-nextaction"
      data-secondary-dropped={String(visible.droppedSecondaryCount)}
      data-testid={testId}
    >
      <span className="ac-nextaction__label">{label}</span>
      {visible.primary !== null ? (
        <span className="ac-nextaction__primary" data-source={visible.primary.source}>
          <strong className="ac-nextaction__primary-label">
            {visible.primary.label}
          </strong>
          {visible.primary.control ?? null}
        </span>
      ) : (
        <span className="ac-nextaction__none" data-testid="nextaction-none">
          {noActionLabel ?? NEXT_ACTION_NONE_LABEL}
        </span>
      )}
      {visible.secondary.length > 0 ? (
        <span className="ac-nextaction__secondary">
          {visible.secondary.map((action, index) => (
            <span
              className="ac-nextaction__secondary-item"
              data-source={action.source}
              key={`${action.label}-${index}`}
            >
              {action.control ?? action.label}
            </span>
          ))}
        </span>
      ) : null}
    </div>
  );
}
